<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDatapoints } from '@/composables/useDatapoints'
import { useSubscription } from '@/features/subscription/composables/useSubscription'
import { useAuth } from '@/composables/useAuth'

// Vue 2 parity (baseSearchService.showModalUserExpired): when the company plan is expired the user
// is hard-blocked by a modal they cannot dismiss — the only exits are renewing (organic self-serve
// plans), contacting support (other plans), or logging off. In Vue 2 this fired on the search pages
// after datapoints loaded; here it is global so an expired account is blocked everywhere.

// Organic onboarding plans (5=free, 6=basic, 7=growth) renew themselves via the subscription page;
// contract/enterprise plans route to support instead.
const ONBOARDING_PLAN_IDS = [5, 6, 7]
const CONTACT_SUPPORT_URL = 'https://www.tami.ai/contact-us/'

const route = useRoute()
const router = useRouter()
const { isPlanExpired, fetchDatapoints } = useDatapoints()
const { currentPlanId, ensureSubscriptionLoaded } = useSubscription()
const { logout } = useAuth()

onMounted(() => {
	// The expiry flag + plan id must be loaded on whatever page the user first lands on.
	void fetchDatapoints()
	void ensureSubscriptionLoaded()
})

const isOnboardingPlan = computed(
	() => currentPlanId.value != null && ONBOARDING_PLAN_IDS.includes(currentPlanId.value),
)

// Never block the subscription page itself — otherwise an organic user could never reach the page
// to renew (Vue 2 escaped the modal via a full-page redirect; this SPA keeps the app mounted).
const visible = computed(() => isPlanExpired.value && route.name !== 'Subscription')

function renew(): void {
	router.push({ name: 'Subscription' })
}
function contactSupport(): void {
	window.open(CONTACT_SUPPORT_URL, '_blank')
}
function logOff(): void {
	void logout()
}
</script>

<template>
	<v-dialog
		:model-value="visible"
		persistent
		max-width="480"
		data-test="account-expired-dialog"
	>
		<v-card
			rounded="lg"
			class="account-expired"
		>
			<v-card-item>
				<div class="d-flex align-center">
					<i class="fa-solid fa-triangle-exclamation account-expired__icon mr-3" />
					<span class="text-h6">Your account has expired</span>
				</div>
			</v-card-item>
			<v-card-text class="text-body-2 text-medium-emphasis">
				<template v-if="isOnboardingPlan">
					We hope you've enjoyed using our app so far! Would you like to continue enjoying all
					the features? Choose one of our plans to keep your account active.
				</template>
				<template v-else>
					If you want to renew your subscription please contact our support.
				</template>
			</v-card-text>
			<v-card-actions class="px-4 pb-3">
				<v-btn
					variant="text"
					class="text-none"
					data-test="account-expired-logout"
					@click="logOff"
				>
					Close
				</v-btn>
				<v-spacer />
				<v-btn
					v-if="isOnboardingPlan"
					color="primary"
					variant="flat"
					class="text-none"
					data-test="account-expired-renew"
					@click="renew"
				>
					Renew Subscription
				</v-btn>
				<v-btn
					v-else
					color="primary"
					variant="flat"
					class="text-none"
					data-test="account-expired-support"
					@click="contactSupport"
				>
					Contact support
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.account-expired {
	&__icon {
		font-size: 24px;
		color: rgb(var(--v-theme-warning));
	}
}
</style>
